<script setup lang="ts">
/**
 * MY — CDD (Customer Due Diligence, M32210) 문서-뷰 패널.
 *
 * 기존 preset 과 달리 "변수 input 폼" 이 아니라 실제 문서(CddXxxDoc)를 렌더하고,
 * 문서 안 변수를 클릭하면 왼쪽 챗봇(person lookup) 또는 상단 입력바(text/date)로 채운다.
 * CDD 는 한 task 에서 여러 건(고객마다) 작성 가능 → 문서 매니저(추가/전환/삭제).
 */
import { ref, computed, provide, watch, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import type { PreSetSelection } from '@/features/startTask/preset_task/shared/preset/types/preset.types';
import type { OfficerDetail } from '@/features/AI-documents/types/docGen.types';
import { getTaskConfigByCode } from '@/features/startTask/api/taskConfigApi';
import { CDD_DOCS, labelize, typeOf, addressGroupOf, comboSourceOf, CDD_SHAREHOLDER_MAX, type CddDocType } from './cdd/cddFields';
import { CDD_DOC_CTX } from './cdd/cddContext';
import { buildCddVariables } from '../mappers/cddMapper';
import { parseMalaysianAddress, parseMyAddressObject, isEmptyMyAddress } from '@/shared/utils/malaysianAddress';
import { comboOptions, resolveNationality, resolveOccupation, resolveCountryOption, type ComboOpt } from './cdd/cddOptions';
import { formatMalaysiaIdForCdd } from './cdd/cddIdFormat';
import { getActiveMembers, getActiveBeneficialOwners } from '@/features/startTask/preset_task/sg/components/sgDocGenEntityApi';
import { buildShareholderRows, SHARE_CLASS_OPTIONS, buildBoRows } from './cdd/cddShareholders';
import { usePresetProgress } from '@/features/startTask/preset_task/shared/preset/composables/usePresetProgress';
import { prefetchB15Cache } from '@/features/startTask/preset_task/shared/preset/lookups/b15NationalityCache';
import { contactsApi } from '@/features/Contact/api/contactsApi';
import ContactRegisterModal from '@/features/startTask/preset_task/shared/preset/components/ContactRegisterModal.vue';
import CddIndividualDoc from './cdd/CddIndividualDoc.vue';
import CddLegalPersonDoc from './cdd/CddLegalPersonDoc.vue';
import CddLegalArrangementDoc from './cdd/CddLegalArrangementDoc.vue';
import AddressInputModal from './cdd/AddressInputModal.vue';

interface CddDoc {
  docType: CddDocType; templateId: string; values: Record<string, string>; checkbox: string; title: string;
  // Legal Person Shareholder/BO 자동입력 — 동적 행 수 + entity 별 Share Class 콤보 옵션 + 마지막 조회 entity
  shRowCount?: number; boRowCount?: number; shareClasses?: ComboOpt[]; sharesEntityCd?: string;
}

const props = defineProps<{
  selection: PreSetSelection;
  entityData: Record<string, any> | null;
  taskTitle: string;
  isReady: boolean;
  currentStep: string;
  selectedPerson?: OfficerDetail | null;
  activePersonContext?: Record<string, any> | null;
  selectedAddress?: { value: string; type: string; position?: string } | null;
  activeAddressContext?: { fieldGroup: string; index: number } | null;
  editTaskId?: number | null;
  /** Preset Studio 셸 안에서 렌더될 때 true — 자체 헤더/엔티티 스트립은 상단 Studio 바로 이동하므로 숨김. */
  studio?: boolean;
  /** 챗봇에서 문서 유형 선택 시 전달 — watch 로 addDoc (ref 명령 호출 대신 reactive). `type:seq` 형태로 중복 방지. */
  pendingDocType?: string;
  /** 챗봇 주소 폼 적용 결과 — watch 로 해당 필드(key) 채움 (모달 대신). */
  appliedAddress?: { key: string; line: string; town: string; state: string; postcode: string; country: string; seq: number } | null;
  /** pick-place: 챗봇 상세카드 copy 클릭으로 보관된 값 (다음 필드 클릭 시 삽입). */
  pendingPick?: { value: string; seq: number } | null;
}>();

const emit = defineEmits<{
  (e: 'back'): void;
  (e: 'task-created', data: { taskId: number; taskTitle: string }): void;
  (e: 'request-person-lookup', ctx: Record<string, any>): void;
  (e: 'change-entity'): void;
  (e: 'change-task-title'): void;
  /** Studio 상단 바 진행률(%) 갱신. */
  (e: 'progress-change', pct: number): void;
  /** studio: 주소 아이콘 클릭 → 챗봇에 주소 입력 폼 표시 요청 (모달 대신). */
  (e: 'request-address-input', key: string): void;
}>();

const router = useRouter();
const { t } = useI18n();
const entityCd = computed(() => String(props.entityData?.entityCd || props.entityData?.icpCd || '').trim());

// Task Title — 패널에서 직접 입력/재입력 (챗봇 값이 있으면 그걸 기본으로, 타이핑하면 로컬 우선).
const taskTitleLocal = ref('');
const titleModel = computed({
  get: () => taskTitleLocal.value || props.taskTitle || '',
  set: (v: string) => { taskTitleLocal.value = v; },
});
const effectiveTitle = computed(() => titleModel.value.trim());

// ─── 초기 셋업 위저드: Task Title → Entity(선택/Skip) → 문서 선택 ───
// 한 화면에 다 나오면 고객이 헷갈려 입력을 못하므로 단계별 안내.
const setupDone = ref(false);
const wizardStep = ref(1); // 1=Task Title, 2=Entity, 3=문서 선택
// Studio 모드에선 셋업(제목/엔티티)을 챗봇이 중앙에서 처리하므로 패널 위저드는 표시하지 않음
// → 항상 에디터(문서 매니저)를 렌더하고, 문서 유형은 에디터의 선택 카드(addDoc)로 고른다.
const showWizard = computed(() => !props.studio && !setupDone.value);
function wizardNext() { if (wizardStep.value < 3) wizardStep.value += 1; }
function wizardBack() { if (wizardStep.value > 1) wizardStep.value -= 1; }
function wizardChoose(k: CddDocType) { addDoc(k); setupDone.value = true; }
const DOC_TYPE_LABELS: Record<CddDocType, string> = {
  individual: 'typeIndividual', legal_person: 'typeLegalPerson', legal_arrangement: 'typeLegalArrangement',
};
const CDD_DOC_TYPES: CddDocType[] = ['individual', 'legal_person', 'legal_arrangement'];
const DOC_TYPE_ICONS: Record<CddDocType, string> = {
  individual: 'mdi-account-outline', legal_person: 'mdi-office-building-outline', legal_arrangement: 'mdi-file-tree-outline',
};

// ─── 문서 매니저 ───────────────────────────────────────────────
const documents = ref<CddDoc[]>([]);
const activeIdx = ref(-1);
const activeDoc = computed(() => documents.value[activeIdx.value] || null);
const addMenu = ref(false);
const renamingIdx = ref(-1); // 탭에서 파일명 인라인 편집 중인 문서 인덱스
const docComponent = computed(() => ({
  individual: CddIndividualDoc, legal_person: CddLegalPersonDoc, legal_arrangement: CddLegalArrangementDoc,
}[activeDoc.value?.docType || 'individual']));

function addDoc(docType: CddDocType) {
  const meta = CDD_DOCS[docType];
  const doc: CddDoc = {
    docType, templateId: meta.templateId, values: {}, checkbox: '', title: '',
  };
  if (docType === 'legal_person') {
    doc.shRowCount = 2;
    doc.boRowCount = 2;
    doc.shareClasses = SHARE_CLASS_OPTIONS;
  }
  documents.value.push(doc);
  activeIdx.value = documents.value.length - 1;
  addMenu.value = false;
  activeKey.value = '';
}
function removeDoc(i: number) {
  documents.value.splice(i, 1);
  if (activeIdx.value >= documents.value.length) activeIdx.value = documents.value.length - 1;
}
// 챗봇 문서유형 선택 → reactive 하게 addDoc (`type:seq` 에서 type 만 추출)
watch(() => props.pendingDocType, (v) => {
  const type = String(v || '').split(':')[0] as CddDocType;
  if (type && CDD_DOC_TYPES.includes(type)) addDoc(type);
});
function autoTitle(d: CddDoc): string {
  const nameKey = d.docType === 'individual' ? 'ind_full_name' : d.docType === 'legal_person' ? 'lp_company_name' : 'la_name';
  return d.values[nameKey] || CDD_DOCS[d.docType].label;
}
function docTitle(d: CddDoc): string {
  return (d.title || '').trim() || autoTitle(d);
}
function progressOf(d: CddDoc): string {
  const keys = CDD_DOCS[d.docType].keys;
  const filled = keys.filter((k) => d.values[k]).length;
  return `${filled}/${keys.length}`;
}
// 전체 진행률(%) — 모든 문서의 채워진 변수 합계. 상단 Studio 바에 노출.
const overallProgress = computed(() => {
  let filled = 0;
  let total = 0;
  for (const d of documents.value) {
    const keys = CDD_DOCS[d.docType].keys;
    total += keys.length;
    filled += keys.filter((k) => d.values[k]).length;
  }
  return total === 0 ? 0 : Math.round((filled / total) * 100);
});
watch(overallProgress, (v) => emit('progress-change', v), { immediate: true });

// ─── 문서 확대 (시니어 가독성) — localStorage 유지 ───────────────
const ZOOM_KEY = 'cdd.docZoom';
const ZOOM_MIN = 100;
const ZOOM_MAX = 175;
const ZOOM_STEP = 25;
function clampZoom(n: number) {
  return Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, Math.round(n / ZOOM_STEP) * ZOOM_STEP));
}
function readStoredZoom(): number {
  try {
    const n = Number(localStorage.getItem(ZOOM_KEY));
    if (Number.isFinite(n)) return clampZoom(n);
  } catch { /* ignore */ }
  return 100;
}
const docZoom = ref(readStoredZoom());
const canZoomIn = computed(() => docZoom.value < ZOOM_MAX);
const canZoomOut = computed(() => docZoom.value > ZOOM_MIN);
function setDocZoom(n: number) {
  docZoom.value = clampZoom(n);
  try { localStorage.setItem(ZOOM_KEY, String(docZoom.value)); } catch { /* ignore */ }
}
function zoomIn() { setDocZoom(docZoom.value + ZOOM_STEP); }
function zoomOut() { setDocZoom(docZoom.value - ZOOM_STEP); }
function zoomReset() { setDocZoom(100); }

// ─── 활성 필드(변수 클릭) ───────────────────────────────────────
const activeKey = ref('');
const activeCheckbox = ref('');
watch(activeIdx, () => { activeCheckbox.value = activeDoc.value?.checkbox || ''; });

const inputKey = ref('');
const inputText = ref('');
const dateMenu = ref(false);

function activate(key: string) {
  activeKey.value = key;
  const t = typeOf(key);
  if (t === 'person' || t === 'entity') {
    emit('request-person-lookup', { fieldGroup: 'cdd', key, docIndex: activeIdx.value, defaultRole: null });
    inputKey.value = '';
  } else {
    inputKey.value = key;
    inputText.value = activeDoc.value?.values[key] || '';
    if (t === 'date') dateMenu.value = true;
  }
}
/** 달력에서 날짜 선택 → DD/MM/YYYY 로 포맷 + 즉시 필드에 커밋. */
function onPickDate(d: any) {
  const date = Array.isArray(d) ? d[0] : d;
  if (!date) return;
  const dt = new Date(date);
  inputText.value = Number.isNaN(dt.getTime())
    ? String(date)
    : `${String(dt.getDate()).padStart(2, '0')}/${String(dt.getMonth() + 1).padStart(2, '0')}/${dt.getFullYear()}`;
  dateMenu.value = false;
  commitInput();
}
function commitInput() {
  if (activeDoc.value && inputKey.value) activeDoc.value.values[inputKey.value] = inputText.value.trim();
  inputKey.value = ''; activeKey.value = '';
}
function skipInput() { inputKey.value = ''; activeKey.value = ''; } // 날짜 등 선택
function setCheckbox(v: string) {
  if (!activeDoc.value) return;
  // 같은 값을 다시 클릭하면 해제(토글)
  const nv = activeDoc.value.checkbox === v ? '' : v;
  activeDoc.value.checkbox = nv; activeCheckbox.value = nv;
}
function setFieldValue(key: string, val: string) {
  if (activeDoc.value) activeDoc.value.values[key] = val;
}

// ── pick-place: 챗봇 상세카드 copy 클릭 → 값 보관 → 문서 필드 클릭 시 그 필드에 삽입 ──
const pendingPickValue = ref('');
watch(() => props.pendingPick, (p) => { pendingPickValue.value = p?.value || ''; });
function placePick(key: string): boolean {
  if (!pendingPickValue.value || !activeDoc.value) return false;
  activeDoc.value.values[key] = pendingPickValue.value;
  pendingPickValue.value = '';
  return true;
}

// ─── 주소 입력 모달 ─────────────────────────────────────────────
const addrModal = ref(false);
const addrKey = ref('');
const addrLabel = ref('');
const addrHasComp = ref(true);
const addrInitial = ref<{ line?: string; town?: string; state?: string; postcode?: string; country?: string }>({});
function openAddress(key: string) {
  // studio: 모달 대신 챗봇에 주소 입력 폼을 띄우도록 요청
  if (props.studio) { addrKey.value = key; emit('request-address-input', key); return; }
  const g = addressGroupOf(key);
  const v = activeDoc.value?.values || {};
  addrKey.value = key;
  addrLabel.value = labelize(key);
  addrHasComp.value = !!(g && g.town); // 컴포넌트(town/state/…) 필드 존재 여부
  addrInitial.value = {
    line: v[key] || '',
    town: g?.town ? v[g.town] : '',
    state: g?.state ? v[g.state] : '',
    postcode: g?.postcode ? v[g.postcode] : '',
    country: g?.country ? v[g.country] : '',
  };
  addrModal.value = true;
}
function applyAddressTo(key: string, a: { line: string; town: string; state: string; postcode: string; country: string }) {
  const doc = activeDoc.value; if (!doc || !key) return;
  const g = addressGroupOf(key);
  if (g?.town) {
    // 컴포넌트 필드 있음 → 앵커=street lines, town/state/postcode/country 는 분리 저장
    const up = (s: string) => String(s || '').trim().toUpperCase();
    doc.values[key] = up(a.line);
    doc.values[g.town] = up(a.town);
    if (g.state) doc.values[g.state] = up(a.state);
    if (g.postcode) doc.values[g.postcode] = up(a.postcode);
    if (g.country) doc.values[g.country] = up(resolveCountryOption(a.country) || a.country);
  } else {
    // 컴포넌트 없는 단일 라인 필드 → 전체 주소를 한 줄로
    doc.values[key] = [a.line, [a.postcode, a.town].filter(Boolean).join(' '), a.state, a.country]
      .map((s) => String(s || '').trim()).filter(Boolean).join(', ').toUpperCase();
  }
}
function applyAddress(a: { line: string; town: string; state: string; postcode: string; country: string }) {
  applyAddressTo(addrKey.value, a); // 모달 경로
}
// studio: 챗봇 주소 폼 적용 → 해당 필드 채움
watch(() => props.appliedAddress, (a) => { if (a?.key) applyAddressTo(a.key, a); });

// ─── Legal Person: Shareholder(s)/Beneficial Owner(s) 자동 입력 + Share Class 콤보 ───
const shRowCountRef = ref(2);
const boRowCountRef = ref(2);
watch(activeIdx, () => {
  shRowCountRef.value = Math.max(1, activeDoc.value?.shRowCount || 2);
  boRowCountRef.value = Math.max(1, activeDoc.value?.boRowCount || 2);
}, { immediate: true });

// share_type 콤보 = Ordinary / Preference / Others 고정 3종 (자동 바인딩 값 유지).
function comboOptionsOf(key: string): ComboOpt[] {
  if (/share_type_\d+$/.test(key)) return SHARE_CLASS_OPTIONS;
  return comboOptions(comboSourceOf(key));
}

function addShareholderRow() {
  const doc = activeDoc.value;
  if (!doc || doc.docType !== 'legal_person') return;
  const cur = Math.max(1, doc.shRowCount || shRowCountRef.value || 1);
  if (cur >= CDD_SHAREHOLDER_MAX) return;
  const next = cur + 1;
  doc.values[`shareholder_name_${next}`] = doc.values[`shareholder_name_${next}`] || '';
  doc.values[`share_type_${next}`] = doc.values[`share_type_${next}`] || '';
  doc.values[`share_pct_${next}`] = doc.values[`share_pct_${next}`] || '';
  doc.shRowCount = next;
  shRowCountRef.value = next;
}

/** 1-based Shareholder 행 삭제 후 뒤 행을 당김. 최소 1행 유지(값만 비움). */
function removeShareholderRow(index: number) {
  const doc = activeDoc.value;
  if (!doc || doc.docType !== 'legal_person') return;
  const total = Math.max(1, doc.shRowCount || shRowCountRef.value || 1);
  if (index < 1 || index > total) return;
  if (total <= 1) {
    doc.values.shareholder_name_1 = '';
    doc.values.share_type_1 = '';
    doc.values.share_pct_1 = '';
    return;
  }
  for (let i = index; i < total; i++) {
    doc.values[`shareholder_name_${i}`] = doc.values[`shareholder_name_${i + 1}`] || '';
    doc.values[`share_type_${i}`] = doc.values[`share_type_${i + 1}`] || '';
    doc.values[`share_pct_${i}`] = doc.values[`share_pct_${i + 1}`] || '';
  }
  doc.values[`shareholder_name_${total}`] = '';
  doc.values[`share_type_${total}`] = '';
  doc.values[`share_pct_${total}`] = '';
  doc.shRowCount = total - 1;
  shRowCountRef.value = total - 1;
}

function addBoRow() {
  const doc = activeDoc.value;
  if (!doc || doc.docType !== 'legal_person') return;
  const cur = Math.max(1, doc.boRowCount || boRowCountRef.value || 1);
  if (cur >= CDD_SHAREHOLDER_MAX) return;
  const next = cur + 1;
  doc.values[`bo_name_${next}`] = doc.values[`bo_name_${next}`] || '';
  doc.values[`bo_relationship_${next}`] = doc.values[`bo_relationship_${next}`] || '';
  doc.boRowCount = next;
  boRowCountRef.value = next;
}

/** 1-based BO 행 삭제 후 뒤 행을 당김. 최소 1행 유지(값만 비움). */
function removeBoRow(index: number) {
  const doc = activeDoc.value;
  if (!doc || doc.docType !== 'legal_person') return;
  const total = Math.max(1, doc.boRowCount || boRowCountRef.value || 1);
  if (index < 1 || index > total) return;
  if (total <= 1) {
    doc.values.bo_name_1 = '';
    doc.values.bo_relationship_1 = '';
    return;
  }
  for (let i = index; i < total; i++) {
    doc.values[`bo_name_${i}`] = doc.values[`bo_name_${i + 1}`] || '';
    doc.values[`bo_relationship_${i}`] = doc.values[`bo_relationship_${i + 1}`] || '';
  }
  doc.values[`bo_name_${total}`] = '';
  doc.values[`bo_relationship_${total}`] = '';
  doc.boRowCount = total - 1;
  boRowCountRef.value = total - 1;
}

// entity 의 현재 유효 member/BO 조회 → shareholder/BO 테이블 자동 채움 (하드코딩·임의값 없음).
/** 같은 entity 에서 BO 표가 빈 채로 남은 경우 1회만 재시도 (무한루프 방지). */
const boRefillTried = new WeakSet<object>();

async function autofillShareholders(doc: CddDoc | null, ecd: string) {
  if (!doc || doc.docType !== 'legal_person' || !ecd) return;

  const boAlreadyFilled = (() => {
    for (let n = 1; n <= CDD_SHAREHOLDER_MAX; n++) {
      if (String(doc.values[`bo_name_${n}`] || '').trim()) return true;
    }
    return false;
  })();
  // 같은 entity 재조회 스킵 — 단, BO 표가 비어 있으면(첫 조회 실패/구필터) BO만 1회 재채움
  const skipFull = doc.sharesEntityCd === ecd;
  if (skipFull && boAlreadyFilled) return;
  if (skipFull && !boAlreadyFilled) {
    if (boRefillTried.has(doc)) return;
    boRefillTried.add(doc);
  }

  const [memRes, boRes] = await Promise.all([
    getActiveMembers(ecd).catch((e) => { console.warn('[CDD] members fetch failed', e); return null; }),
    getActiveBeneficialOwners(ecd).catch((e) => { console.warn('[CDD] BO fetch failed', e); return null; }),
  ]);
  const members = (memRes as any)?.data?.members || (memRes as any)?.members || [];
  const bos = (boRes as any)?.data?.beneficialOwners || (boRes as any)?.beneficialOwners || [];
  const shRows = buildShareholderRows(members).slice(0, CDD_SHAREHOLDER_MAX);
  const boRows = buildBoRows(bos, shRows.map((r) => r.name)).slice(0, CDD_SHAREHOLDER_MAX);
  console.info('[CDD] autofill shareholders/BO', {
    entityCd: ecd,
    skipFull,
    boAlreadyFilled,
    members: Array.isArray(members) ? members.length : 0,
    bos: Array.isArray(bos) ? bos.length : 0,
    shRows: shRows.length,
    boRows: boRows.map((r) => ({ name: r.name, relationship: r.relationship })),
    boTypeSample: (Array.isArray(bos) ? bos : []).slice(0, 5).map((b: any) => ({
      name: b?.name || b?.fullName,
      boTypeId: b?.boTypeId,
      boTypeDescription: b?.boTypeDescription,
      isSeniorManagement: b?.isSeniorManagement,
    })),
  });

  if (!skipFull) {
    // entity 변경 refill — 기존 행 값 초기화 후 데이터로 채움
    for (let n = 1; n <= CDD_SHAREHOLDER_MAX; n++) {
      doc.values[`shareholder_name_${n}`] = ''; doc.values[`share_type_${n}`] = ''; doc.values[`share_pct_${n}`] = '';
      doc.values[`bo_name_${n}`] = ''; doc.values[`bo_relationship_${n}`] = '';
    }
    shRows.forEach((r, i) => {
      doc.values[`shareholder_name_${i + 1}`] = r.name;
      doc.values[`share_type_${i + 1}`] = r.shareType;
      doc.values[`share_pct_${i + 1}`] = r.pct;
    });
    doc.shRowCount = Math.max(2, shRows.length);
    doc.shareClasses = SHARE_CLASS_OPTIONS;
  } else {
    // BO만 재채움 — 주주 표는 유지
    for (let n = 1; n <= CDD_SHAREHOLDER_MAX; n++) {
      doc.values[`bo_name_${n}`] = '';
      doc.values[`bo_relationship_${n}`] = '';
    }
  }

  boRows.forEach((r, i) => {
    doc.values[`bo_name_${i + 1}`] = r.name;
    doc.values[`bo_relationship_${i + 1}`] = r.relationship;
  });
  doc.boRowCount = Math.max(1, boRows.length || 2);

  // Senior Management — isSeniorManagement 체크된 BO 이름
  const smNames = [...new Set(
    (Array.isArray(bos) ? bos : [])
      .filter((b: any) => b?.isSeniorManagement)
      .map((b: any) => String(b?.name || b?.contactName || b?.fullName || '').trim())
      .filter(Boolean),
  )];
  if (smNames.length && !String(doc.values.lp_senior_management || '').trim()) {
    doc.values.lp_senior_management = smNames.join(', ');
  }

  doc.sharesEntityCd = ecd;
  if (doc === activeDoc.value) {
    if (!skipFull) shRowCountRef.value = doc.shRowCount || 2;
    boRowCountRef.value = doc.boRowCount;
  }
}

// entity 선택/변경 또는 Legal Person 문서 활성화 시 자동 채움
watch([entityCd, activeIdx], () => {
  if (activeDoc.value?.docType === 'legal_person' && entityCd.value) {
    autofillShareholders(activeDoc.value, entityCd.value);
  }
}, { immediate: true });

provide(CDD_DOC_CTX, {
  valueOf: (k) => activeDoc.value?.values[k] || '',
  labelOf: labelize, typeOf, activeKey, activate, setFieldValue, openAddress,
  checkboxValue: activeCheckbox, setCheckbox,
  comboOptionsOf, shareholderRowCount: shRowCountRef, boRowCount: boRowCountRef,
  addShareholderRow, removeShareholderRow, addBoRow, removeBoRow,
  pendingPickValue, placePick,
});

// ─── Shareholder 테이블 검증 (경고만, 문서 생성 차단 안 함) ───
const shareholderWarnings = computed<string[]>(() => {
  const doc = activeDoc.value;
  if (!doc || doc.docType !== 'legal_person') return [];
  const warns: string[] = [];
  const classes = new Set(SHARE_CLASS_OPTIONS.map((o) => o.value.toUpperCase()));
  let sum = 0;
  const rows = Math.max(1, doc.shRowCount || 2);
  for (let n = 1; n <= rows; n++) {
    const name = (doc.values[`shareholder_name_${n}`] || '').trim();
    const type = (doc.values[`share_type_${n}`] || '').trim();
    const pctRaw = (doc.values[`share_pct_${n}`] || '').trim();
    if (!name) continue;
    if (!type) warns.push(`행 ${n}: "${name}" — Types of Shares 미입력`);
    if (!pctRaw) warns.push(`행 ${n}: "${name}" — Percentage 미입력`);
    const pctNum = parseFloat(pctRaw.replace('%', ''));
    if (pctRaw && (!Number.isFinite(pctNum) || pctNum <= 0 || pctNum > 100)) warns.push(`행 ${n}: Percentage 는 0 초과 100 이하 숫자여야 함`);
    if (Number.isFinite(pctNum)) sum += pctNum;
    if (type && !classes.has(type.toUpperCase())) warns.push(`행 ${n}: "${type}" 는 Ordinary / Preference / Others 중 하나여야 함`);
  }
  if (sum > 100.01) warns.push(`Percentage 합계(${Math.round(sum * 100) / 100}%)가 100% 를 초과`);
  return warns;
});

// ─── 사람/회사 상세 → 문서 필드 채움 (챗봇 선택 + Contact 등록 공용) ───
function applyPersonToDoc(doc: CddDoc, key: string, a: any) {
  const put = (k: string, val: string) => { if (val && CDD_DOCS[doc.docType].keys.includes(k)) doc.values[k] = val; };

  const name = a.name || a.contactName || [a.firstName, a.middleName, a.surname].filter(Boolean).join(' ') || '';
  doc.values[key] = name;

  // 주소 컴포넌트 — 서버 JSON(myResidential) 우선, 없으면 문자열 분리. 확신 없으면 빈칸.
  const fullAddr = a.residentialAddress || a.primaryAddress || '';
  const res = a.myResidential && !isEmptyMyAddress(a.myResidential) ? a.myResidential : parseMalaysianAddress(fullAddr);

  // ── 회사(Legal Person / Legal Arrangement) 선택 → 회사 필드 채움 ──
  if (typeOf(key) === 'entity') {
    const base = key.startsWith('lp_') ? 'lp' : key.startsWith('la_') ? 'la' : '';
    if (!base) return;
    put(`${base}_registration_no`, a.contactRegNo || a.registrationNo || a.regNo || '');
    if (!isEmptyMyAddress(res)) {
      put(`${base}_registered_address`, res.street || fullAddr);
      put(`${base}_town`, res.town); put(`${base}_state`, res.state);
      put(`${base}_postcode`, res.postcode); put(`${base}_country`, resolveCountryOption(res.country));
    } else {
      put(`${base}_registered_address`, fullAddr);
    }
    const ctry = resolveCountryOption(res.country);
    put(`${base}_country_of_incorporation`, ctry); // LP
    put(`${base}_country_of_registration`, ctry);  // LA
    return;
  }

  // ── 개인(person) 선택 → 개인 필드 채움 ──
  const prefix = key.replace(/(_full_name|_name)$/, '');
  const set = (suffix: string, val: string) => put(`${prefix}${suffix}`, val);
  // NRIC 12자리면 XXXXXX-XX-XXXX, 여권 등은 원문 (소스에 하이픈 유무와 무관하게 통일)
  const id = formatMalaysiaIdForCdd(a.nricNo || a.passportNo || a.contactRegNo || '');
  // Nationality: 코드(B15135)→옵션 매칭(없으면 빈칸=콤보에서 선택). Occupation: positionName('Contact') 금지.
  // 이름(예: MALAYSIA) 먼저 시도 → 실패 시 코드(B15xxx) 시도. officer/contact 소스 모두, 비-MY 국적도 견고하게 resolve.
  const nat = resolveNationality(String(a.nationalityName || '')) || resolveNationality(String(a.nationality || ''));
  const occ = resolveOccupation(a.occupation || a.businessOccupation || '');
  const contact = [a.areaCode, a.mobileNo].filter(Boolean).join(' ').trim() || a.mobileNo || '';
  set('_ic_passport_no', id); set('_id', id);
  set('_dob', a.dateOfBirth || '');
  set('_nationality', nat);
  set('_occupation', occ); set('_occupation_type', occ);
  set('_contact_no', contact);
  // 서명란(signer) — 이름 선택 시 직책(Designation)도 함께. positionName('Contact'는 제외), 없으면 occupation.
  const desig = a.positionName && a.positionName !== 'Contact' ? a.positionName : occ;
  set('_designation', desig);
  // 서명(선언) 영역 — 주 신고자(개인 본인=ind_full_name, 또는 대리인=pt_full_name) 선택 시 Name·Designation 동기화.
  //   (signer_name 을 직접 선택한 경우는 위 prefix='signer' 로직이 이미 처리)
  if (key === 'ind_full_name' || key === 'pt_full_name') {
    put('signer_name', name);
    put('signer_designation', desig);
  }

  if (!isEmptyMyAddress(res)) {
    set('_residential_address', res.street || fullAddr); set('_address', res.street || fullAddr);
    set('_town', res.town); set('_state', res.state); set('_postcode', res.postcode); set('_country', resolveCountryOption(res.country));
  } else {
    set('_residential_address', fullAddr); set('_address', fullAddr);
  }
  // Mailing — 별도 주소(alternateAddress)가 있을 때만 (없으면 residential 복사 금지)
  const mailFull = a.alternateAddress || a.mailingAddress || '';
  const mail = a.myMailing && !isEmptyMyAddress(a.myMailing) ? a.myMailing : (mailFull ? parseMalaysianAddress(mailFull) : null);
  if (mail && !isEmptyMyAddress(mail)) {
    set('_mailing_address', mail.street || mailFull); set('_mailing_town', mail.town);
    set('_mailing_state', mail.state); set('_mailing_postcode', mail.postcode); set('_mailing_country', resolveCountryOption(mail.country));
  }
}

// 챗봇 person/entity 선택 → 활성 컨텍스트 문서에 채움
watch(() => props.selectedPerson, (p) => {
  const ctx = props.activePersonContext;
  if (!p || !ctx || ctx.fieldGroup !== 'cdd') return;
  const doc = documents.value[ctx.docIndex]; if (!doc) return;
  applyPersonToDoc(doc, ctx.key, p as any);
});

// ─── Contact 등록 (AOD 참고) — 신규 고객 contact 생성 후 활성 문서 자동 채움 ───
const contactRegisterOpen = ref(false);
function mainNameKey(dt: CddDocType): string {
  return dt === 'individual' ? 'ind_full_name' : dt === 'legal_person' ? 'lp_company_name' : 'la_name';
}
async function onContactRegistered(payload: { contactCd: string; type?: string; data?: any }) {
  contactRegisterOpen.value = false;
  const doc = activeDoc.value; const cd = payload?.contactCd;
  if (!doc || !cd) return;
  try {
    const raw: any = await contactsApi.getContactById(cd, entityCd.value ? { entityCd: entityCd.value } : undefined);
    const c = raw?.data ?? raw ?? {};
    const g = (camel: string, upper: string) => String(c[camel] ?? c[upper] ?? '').trim();
    applyPersonToDoc(doc, mainNameKey(doc.docType), {
      name: g('contactName', 'CONTACT_NAME'),
      contactRegNo: g('contactRegNo', 'CONTACT_REG_NO'),
      nricNo: g('contactRegNo', 'CONTACT_REG_NO'),
      dateOfBirth: (g('dateOfBirth', 'DATE_OF_BIRTH') || '').split(/[ T]/)[0],
      nationality: g('nationalityCd', 'NATIONALITY_CD') || g('nationalityName', 'NATIONALITY_NAME'),
      occupation: g('occupation', 'PROFILE_OCCUPATION') || g('businessOccupation', 'BUSINESS_OCCUPATION'),
      areaCode: g('areaCode', 'AREA_CODE'), mobileNo: g('mobileNo', 'MOBILE_NO'),
      positionName: 'Contact',
      myResidential: parseMyAddressObject(c.registeredAddress ?? c.RESIDENTIAL_ADDRESS ?? c.residential_address),
    });
  } catch (e) {
    console.warn('[CDD] contact register autofill 실패', e);
  }
}

// ─── Create / Update Task ──────────────────────────────────────
const editMode = computed(() => !!props.editTaskId); // Edit 진입 시 Update Task
const taskConfigSteps = ref<string[]>([]);
const creating = ref(false);
const progress = ref(''); // 진행/오류 메시지 (조용한 실패 방지)
// AOD 동일 — creating/progress 를 호스트 중앙 로딩 오버레이(presetProgress)로 흘려보냄
const presetProgress = usePresetProgress();
watch(creating, (v) => { presetProgress.busy = v; });
watch(progress, (v) => { presetProgress.message = v; });
onMounted(async () => {
  if (props.editTaskId) setupDone.value = true; // Edit 진입은 위저드 skip
  prefetchB15Cache(); // 국적 baseCode(B15) 코드명 캐시 미리 로드 → 사람 상세에서 코드 대신 이름 표시
  const code = props.selection?.task?.TASK_CODE;
  if (code) { try { const cfg = await getTaskConfigByCode(code); taskConfigSteps.value = (cfg as any)?.processSteps || []; } catch { /* noop */ } }
});

// 문서 타입 → task config 에 첨부된 실제 템플릿(tmpId) 매칭. TMP00x 은 실제 tmpId 가 아님.
const DOC_TYPE_PATTERNS: Record<CddDocType, RegExp> = {
  individual: /individual|natural\s*person/i,
  legal_person: /legal\s*person|corporate|\bcompany\b/i,
  legal_arrangement: /legal\s*arrangement|arrangement|\btrust\b/i,
};
const availableTemplates = computed(() => (props.selection?.documents || []).filter((d) => d.tmpId));
function resolveTmpId(docType: CddDocType): number | null {
  const docs = availableTemplates.value;
  const hit = docs.find((d) => DOC_TYPE_PATTERNS[docType].test(d.templateName || ''));
  if (hit?.tmpId) return hit.tmpId;
  if (docs.length === 1) return docs[0].tmpId; // 템플릿 1개뿐이면 그걸 사용
  return null;
}

async function handleCreateTask() {
  if (creating.value) return;
  if (!documents.value.length) { progress.value = t('cdd.errNoDoc'); return; }
  if (!effectiveTitle.value) { progress.value = t('cdd.errNoTitle'); return; }
  // CDD 는 신규 고객 대상이라 entity 미선택 허용 (백엔드가 ENTITY_CD='N/A' 로 저장).

  // 각 문서의 실제 템플릿 매칭 선검증 — 못 찾으면 어떤 템플릿이 있는지 안내
  const resolved = documents.value.map((d) => ({ d, tmpId: resolveTmpId(d.docType) }));
  const missing = resolved.filter((r) => !r.tmpId);
  if (missing.length) {
    const avail = availableTemplates.value.map((x) => x.templateName).join(', ') || '(none)';
    progress.value = `${t('cdd.errNoTemplate')}: ${missing.map((m) => CDD_DOCS[m.d.docType].label).join(', ')} — [${avail}]`;
    return;
  }

  creating.value = true;
  progress.value = editMode.value ? t('cdd.updating') : t('cdd.creating');
  try {
    const steps = (taskConfigSteps.value || []).map((s) => String(s).toLowerCase());
    const taskFlow = {
      createDocument: steps.length === 0 ? true : steps.includes('doc'),
      requestTSign: steps.includes('t-sign'),
      proceedWithEFiling: steps.includes('e-filing'),
    };
    // Edit 복원용 스냅샷 + entity (contact-only CDD 는 entityCd 없음 → null 허용)
    const officerData = { entityCd: entityCd.value || null, presetForm: gatherState() };

    let taskId: number | null = null;
    if (editMode.value) {
      // ── Update 분기 (AOD 동일) — 기존 task 데이터/제목 갱신 후 문서 교체 ──
      taskId = Number(props.editTaskId) || null;
      if (!taskId) { progress.value = `${t('cdd.errCreate')}: invalid edit task id`; creating.value = false; return; }
      try {
        await fetch(`/api/startTask/tasks/${taskId}/data`, {
          method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ taskData: { formData: officerData } }),
        });
        await fetch(`/api/startTask/tasks/${taskId}/title`, {
          method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ taskTitle: effectiveTitle.value }),
        });
      } catch (err: any) {
        progress.value = `${t('cdd.errCreate')}: ${err?.message || 'update failed'}`;
        creating.value = false; return;
      }
    } else {
      // ── Create 분기 ──
      const taskRes = await fetch('/api/startTask/tasks', {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          entityCd: entityCd.value || null,
          taskTypeCd: props.selection.task.TASK_CODE,
          taskTypeName: props.selection.task.TASK_NAME,
          taskTitle: effectiveTitle.value, taskFlow, officerData,
        }),
      }).then((r) => r.json());
      taskId = taskRes?.data?.taskId || taskRes?.taskId;
      if (!taskId) {
        const be = taskRes?.error?.message || taskRes?.message || 'no taskId';
        // TASK002 는 백엔드가 상세사유(entityCd 누락 / TASK_CONFIG 미존재)를 숨김 → 힌트 병기
        const hint = /TASK002/.test(be) ? ` — ${t('cdd.errTask002Hint')} (${props.selection.task.TASK_CODE || '?'})` : '';
        progress.value = `${t('cdd.errCreate')}: ${be}${hint}`;
        creating.value = false; return;
      }
    }
    progress.value = t('cdd.savingDocs');

    // 한 task 에 여러 CDD 문서 — 각 문서 = 매칭된 tmpId + 개별 formData/label (backend 가 documents[] 루프)
    const docsToSave = resolved.map(({ d, tmpId }) => {
      const formData = buildCddVariables(d);
      if (d.docType === 'legal_person') {
        console.info('[CDD] repeating map → formData', {
          templateId: tmpId,
          shareholders: Array.isArray(formData.shareholders) ? formData.shareholders.length : 0,
          bo: Array.isArray(formData.bo) ? formData.bo.length : 0,
          shareholdersPreview: (formData.shareholders || []).slice(0, 2),
          boPreview: (formData.bo || []).slice(0, 2),
        });
      }
      return {
        templateId: tmpId,
        label: `${CDD_DOCS[d.docType].label} - ${docTitle(d)}`,
        personName: docTitle(d),
        entityCd: entityCd.value || undefined,
        formData,
      };
    });
    try {
      const saveRes = await fetch('/api/aiDocuments/incorporation/prepare-documents', {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ taskId, documents: docsToSave, replaceExisting: editMode.value }),
      }).then((r) => r.json());
      if (!saveRes?.success) progress.value = `${t('cdd.warnDocs')}: ${saveRes?.message || 'unknown'}`;
    } catch (err: any) {
      console.warn('[CDD] document save failed:', err);
    }

    // 문서 상태 마킹 (다른 preset 과 동일 시퀀스)
    try {
      await fetch(`/api/startTask/tasks/${taskId}/document-status`, {
        method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' },
      });
    } catch { /* ignore */ }
    try {
      await fetch('/api/startTask/preset/use-log', {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ presetTaskId: props.selection.task.PRESET_TASK_ID, taskId }),
      });
    } catch { /* ignore */ }
    // 다음 화면 prefetch
    try {
      progress.value = t('cdd.nextScreen');
      const [taskDetailRes, docListRes] = await Promise.all([
        fetch(`/api/startTask/tasks/${taskId}`, { credentials: 'include' }).then((r) => r.json()).catch(() => null),
        fetch(`/api/startTask/doc-cust-save/${taskId}`, { credentials: 'include' }).then((r) => r.json()).catch(() => null),
      ]);
      sessionStorage.setItem(`presetTaskDetailPrefetch:${taskId}`, JSON.stringify({ taskDetail: taskDetailRes, docList: docListRes }));
    } catch { /* ignore */ }

    emit('task-created', { taskId, taskTitle: effectiveTitle.value });
    router.push({ name: 'PreSetTaskDetail', query: { taskId: String(taskId) } });
  } catch (e: any) {
    console.error('[CDD] create task 실패', e);
    progress.value = `${t('cdd.errCreate')}: ${e?.message || e}`;
  } finally {
    creating.value = false;
  }
}

// ─── Edit gather/restore ───────────────────────────────────────
function gatherState() { return { documents: documents.value }; }
function restoreState(s: any) { if (s?.documents) { documents.value = s.documents; activeIdx.value = 0; setupDone.value = true; } }
defineExpose({ gatherState, restoreState, addDoc, createTask: handleCreateTask });
</script>

<template>
  <div class="cdd-panel" :class="{ 'cdd-studio': studio }">
    <!-- Studio 모드에선 task명/back 이 상단 Studio 바에 있으므로 자체 헤더 숨김 -->
    <div v-if="!studio" class="co-header">
      <div>
        <div class="co-title">{{ selection.task.TASK_NAME }}</div>
        <div class="co-subtitle">{{ t('cdd.subtitle') }}</div>
      </div>
      <v-btn variant="text" size="small" class="text-none" @click="emit('back')"><v-icon start size="16">mdi-arrow-left</v-icon>{{ t('cdd.back') }}</v-btn>
    </div>

    <!-- ── 초기 셋업 위저드: Task Title → Entity(선택/Skip) → 문서 선택 ── -->
    <div v-if="showWizard" class="cdd-wizard">
      <div class="wz-steps">
        <div class="wz-step" :class="{ active: wizardStep === 1, done: wizardStep > 1 }"><span class="wz-num">1</span>{{ t('cdd.wzStepTitle') }}</div>
        <div class="wz-bar" :class="{ done: wizardStep > 1 }" />
        <div class="wz-step" :class="{ active: wizardStep === 2, done: wizardStep > 2 }"><span class="wz-num">2</span>{{ t('cdd.wzStepEntity') }}</div>
        <div class="wz-bar" :class="{ done: wizardStep > 2 }" />
        <div class="wz-step" :class="{ active: wizardStep === 3 }"><span class="wz-num">3</span>{{ t('cdd.wzStepDoc') }}</div>
      </div>

      <!-- Step 1: Task Title -->
      <div v-if="wizardStep === 1" class="wz-card">
        <div class="wz-h">{{ t('cdd.wzTitleH') }}</div>
        <div class="wz-sub">{{ t('cdd.wzTitleSub') }}</div>
        <v-text-field
          v-model="titleModel" :placeholder="t('cdd.taskTitlePh')" variant="outlined" density="comfortable"
          hide-details autofocus class="wz-input" @keydown.enter="effectiveTitle && wizardNext()"
        />
        <div class="wz-actions">
          <v-spacer />
          <v-btn color="primary" class="text-none" :disabled="!effectiveTitle" append-icon="mdi-arrow-right" @click="wizardNext">{{ t('cdd.next') }}</v-btn>
        </div>
      </div>

      <!-- Step 2: Entity (선택 또는 Skip — 추후 변경 가능) -->
      <div v-else-if="wizardStep === 2" class="wz-card">
        <div class="wz-h">{{ t('cdd.wzEntityH') }}</div>
        <div class="wz-sub">{{ t('cdd.wzEntitySub') }}</div>
        <div v-if="entityData?.companyName || entityData?.entityName" class="wz-entity">
          <v-icon color="primary">mdi-domain</v-icon>
          <span>{{ entityData?.companyName || entityData?.entityName }}</span>
          <v-btn variant="tonal" size="small" class="text-none ml-auto" @click="emit('change-entity')">{{ t('cdd.change') }}</v-btn>
        </div>
        <v-btn v-else variant="tonal" color="primary" class="text-none wz-selectbtn" prepend-icon="mdi-domain" @click="emit('change-entity')">{{ t('cdd.wzSelectEntity') }}</v-btn>
        <div class="wz-actions">
          <v-btn variant="text" class="text-none" prepend-icon="mdi-arrow-left" @click="wizardBack">{{ t('cdd.back') }}</v-btn>
          <v-spacer />
          <v-btn variant="text" class="text-none" @click="wizardNext">{{ t('cdd.wzSkipEntity') }}</v-btn>
          <v-btn color="primary" class="text-none" append-icon="mdi-arrow-right" @click="wizardNext">{{ t('cdd.next') }}</v-btn>
        </div>
      </div>

      <!-- Step 3: 문서 선택 -->
      <div v-else class="wz-card wz-card-wide">
        <div class="wz-h">{{ t('cdd.chooseTitle') }}</div>
        <div class="wz-sub">{{ t('cdd.chooseSub') }}</div>
        <div class="cc-cards">
          <button v-for="k in CDD_DOC_TYPES" :key="k" class="cc-card" @click="wizardChoose(k)">
            <v-icon size="30" color="primary">{{ DOC_TYPE_ICONS[k] }}</v-icon>
            <div class="cc-card-label">{{ t('cdd.' + DOC_TYPE_LABELS[k]) }}</div>
            <div class="cc-card-desc">{{ t('cdd.desc_' + k) }}</div>
          </button>
        </div>
        <div class="wz-actions">
          <v-btn variant="text" class="text-none" prepend-icon="mdi-arrow-left" @click="wizardBack">{{ t('cdd.back') }}</v-btn>
        </div>
      </div>
    </div>

    <!-- ── 에디터 (셋업 완료 후): Task Title / Entity / 문서 매니저 ── -->
    <template v-else>
    <!-- Task Title (패널에서 직접 입력/재입력) — studio 에선 상단 바에 있으므로 숨김 -->
    <div v-if="!studio" class="co-title-row">
      <span class="ct-label">{{ t('cdd.taskTitle') }}</span>
      <v-text-field
        v-model="titleModel" :placeholder="t('cdd.taskTitlePh')" variant="outlined" density="compact"
        hide-details class="ct-input"
      />
    </div>

    <!-- Entity — studio 에선 상단 바 칩으로 표시하므로 숨김 -->
    <div v-if="!studio" class="co-entity">
      <v-icon size="18" color="primary">mdi-domain</v-icon>
      <span>{{ entityData?.companyName || entityData?.entityName || t('cdd.entity') }}</span>
      <v-btn variant="tonal" size="x-small" class="text-none ml-auto" @click="emit('change-entity')">{{ t('cdd.change') }}</v-btn>
    </div>

    <!-- 활성 text/date 입력바 -->
    <div v-if="inputKey" class="co-inputbar">
      <div class="lbl">{{ labelize(inputKey) }} <span v-if="typeOf(inputKey) === 'date'" class="opt">({{ t('cdd.optional') }})</span></div>
      <div class="row">
        <v-menu v-if="typeOf(inputKey) === 'date'" v-model="dateMenu" :close-on-content-click="false">
          <template #activator="{ props: mp }">
            <v-text-field v-bind="mp" v-model="inputText" placeholder="DD/MM/YYYY" variant="outlined" density="compact" hide-details append-inner-icon="mdi-calendar" />
          </template>
          <v-date-picker @update:model-value="onPickDate" />
        </v-menu>
        <v-text-field v-else v-model="inputText" :placeholder="labelize(inputKey)" variant="outlined" density="compact" hide-details autofocus @keydown.enter="commitInput" />
        <v-btn color="primary" size="small" variant="flat" @click="commitInput">{{ t('cdd.set') }}</v-btn>
        <v-btn v-if="typeOf(inputKey) === 'date'" variant="text" size="small" @click="skipInput">{{ t('cdd.skip') }}</v-btn>
      </div>
    </div>

    <!-- 문서 매니저 탭 -->
    <div class="co-docs">
      <div v-for="(d, i) in documents" :key="i" class="doc-tab" :class="{ active: i === activeIdx }" @click="activeIdx = i">
        <span class="dt-type">{{ t('cdd.' + DOC_TYPE_LABELS[d.docType]) }}</span>
        <input
          v-if="renamingIdx === i" v-model="d.title" class="dt-rename" :placeholder="autoTitle(d)" autofocus
          @click.stop @keydown.enter="renamingIdx = -1" @keydown.esc="renamingIdx = -1" @blur="renamingIdx = -1"
        />
        <span v-else class="dt-name" :title="t('cdd.renameHint')" @dblclick.stop="renamingIdx = i">{{ docTitle(d) }}</span>
        <span class="dt-prog">{{ progressOf(d) }}</span>
        <v-icon size="13" class="dt-edit" :title="t('cdd.rename')" @click.stop="renamingIdx = i">mdi-pencil-outline</v-icon>
        <v-icon size="13" class="dt-del" @click.stop="removeDoc(i)">mdi-close</v-icon>
      </div>
      <v-menu v-model="addMenu">
        <template #activator="{ props: mp }">
          <v-btn v-bind="mp" size="small" variant="tonal" color="primary" class="text-none" prepend-icon="mdi-plus">{{ t('cdd.addCdd') }}</v-btn>
        </template>
        <v-list density="compact">
          <v-list-item v-for="k in CDD_DOC_TYPES" :key="k" :prepend-icon="DOC_TYPE_ICONS[k]" @click="addDoc(k)">
            <v-list-item-title>{{ t('cdd.' + DOC_TYPE_LABELS[k]) }}</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
      <!-- 신규 Contact 등록 (AOD 참고) — 시스템에 없는 고객을 등록 후 활성 문서 자동 채움 -->
      <v-btn size="small" variant="outlined" color="primary" class="text-none" prepend-icon="mdi-account-plus-outline" @click="contactRegisterOpen = true">{{ t('cdd.registerContact') }}</v-btn>
      <div v-if="activeDoc" class="cdd-zoom" role="group" :aria-label="t('cdd.zoomIn')">
        <button type="button" class="cdd-zoom-btn" :disabled="!canZoomOut" :title="t('cdd.zoomOut')" @click="zoomOut">A−</button>
        <button type="button" class="cdd-zoom-pct" :title="t('cdd.zoomReset')" @click="zoomReset">{{ docZoom }}%</button>
        <button type="button" class="cdd-zoom-btn" :disabled="!canZoomIn" :title="t('cdd.zoomIn')" @click="zoomIn">A+</button>
      </div>
    </div>

    <!-- 문서 본문 -->
    <div class="co-doc-body">
      <!-- studio: 문서 유형은 챗봇에서 고르므로 옛날 선택 카드 대신 깔끔한 안내 -->
      <div v-if="!activeDoc && studio" class="cc-studio-empty">
        <v-icon size="34" color="#c4ccd6">mdi-file-document-plus-outline</v-icon>
        <div class="ccs-title">{{ t('cdd.chooseTitle') }}</div>
        <div class="ccs-sub">Pick a document type in the assistant, or press “+ Add CDD” above.</div>
      </div>
      <div v-else-if="!activeDoc" class="co-choose">
        <div class="cc-title">{{ t('cdd.chooseTitle') }}</div>
        <div class="cc-sub">{{ t('cdd.chooseSub') }}</div>
        <div class="cc-cards">
          <button v-for="k in CDD_DOC_TYPES" :key="k" class="cc-card" @click="addDoc(k)">
            <v-icon size="30" color="primary">{{ DOC_TYPE_ICONS[k] }}</v-icon>
            <div class="cc-card-label">{{ t('cdd.' + DOC_TYPE_LABELS[k]) }}</div>
            <div class="cc-card-desc">{{ t('cdd.desc_' + k) }}</div>
          </button>
        </div>
      </div>
      <div v-else>
        <div class="co-doctitle">
          <span class="dt-label">{{ t('cdd.documentTitle') }}</span>
          <v-text-field v-model="activeDoc.title" :placeholder="autoTitle(activeDoc)" variant="outlined" density="compact" hide-details class="dt-input" />
        </div>
        <!-- Shareholder 검증 경고 (문서 생성 차단 안 함) -->
        <div v-if="shareholderWarnings.length" class="cdd-shwarn">
          <v-icon size="16" color="#d97706">mdi-alert-outline</v-icon>
          <ul><li v-for="(w, i) in shareholderWarnings" :key="i">{{ w }}</li></ul>
        </div>
        <div class="paper-zoom" :style="{ zoom: docZoom / 100 }">
          <div class="paper">
            <component :is="docComponent" :key="activeIdx" />
          </div>
        </div>
      </div>
    </div>

    <!-- Footer -->
    <!-- studio 에선 Back/Create 가 상단 바(Back, Generate)에 있으므로, 오류 메시지가 있을 때만 노출 -->
    <div v-if="!studio || progress" class="co-footer">
      <v-btn v-if="!studio" variant="outlined" class="text-none" @click="emit('back')">{{ t('cdd.back') }}</v-btn>
      <div class="co-foot-right">
        <span v-if="progress" class="co-progress" :class="{ err: progress.startsWith('Error') || progress.includes('⚠') }">{{ progress }}</span>
        <v-btn v-if="!studio" color="primary" class="text-none" :loading="creating" :disabled="creating || !documents.length" :prepend-icon="editMode ? 'mdi-content-save' : 'mdi-check'" @click="handleCreateTask">{{ editMode ? t('cdd.updateTask') : t('cdd.createTask') }}</v-btn>
      </div>
    </div>
    </template>

    <!-- 말레이시아 주소 입력 모달 -->
    <AddressInputModal
      v-model="addrModal" :label="addrLabel" :has-components="addrHasComp" :initial="addrInitial"
      @apply="applyAddress"
    />

    <!-- 신규 Contact 등록 모달 (AOD 공용) -->
    <ContactRegisterModal v-model="contactRegisterOpen" @created="onContactRegistered" />
  </div>
</template>

<style scoped>
/* height:100% 제거 — 문서가 자연 높이로 길게 흐르고 호스트 panel-right(overflow-y:auto)가 스크롤 */
.cdd-panel { display:flex; flex-direction:column; }
/* Studio 셸 안에서는 중앙 문서 카드처럼 폭을 잡고 흰 카드 위에 흐르게 */
/* Studio: 문서 영역. 흰 카드 배경 없이(탭/제목이 페이지에 자연스럽게) 종이(.paper)만 문서로 보이게.
   flex 부모(ps-docarea) 안에서 줄지 않게(자연 높이) 해야 docarea 가 스크롤됨. */
.cdd-studio { flex:0 0 auto; width:min(1000px, 100%); background:transparent; }
.cdd-studio .co-docs { background:transparent; border-bottom:none; padding-left:0; padding-right:0; }
.cdd-studio .co-doctitle { max-width:none; }
.cdd-shwarn { max-width:820px; margin:0 auto 12px; display:flex; align-items:flex-start; gap:8px; background:#fffbeb; border:1px solid #fde68a; border-radius:10px; padding:10px 12px; font-size:12.5px; color:#92610a; }
.cdd-shwarn ul { margin:0; padding-left:16px; } .cdd-shwarn li { margin:1px 0; }
.cdd-studio .co-doc-body { background:transparent; padding:0; }
.co-header { display:flex; align-items:center; justify-content:space-between; padding:12px 16px; border-bottom:1px solid #eef0f6; }
.co-title { font-weight:700; font-size:15px; } .co-subtitle { font-size:12px; color:#8a94a6; }
.co-title-row { display:flex; align-items:center; gap:10px; padding:10px 14px; border-bottom:1px solid #eef0f6; }
.co-title-row .ct-label { font-size:12px; font-weight:700; color:#5b6472; white-space:nowrap; }
.co-title-row .ct-input { max-width:520px; }
.co-entity { display:flex; align-items:center; gap:8px; background:#f0f5ff; padding:8px 14px; font-size:13px; }
.co-inputbar { padding:10px 14px; background:#fafbfe; border-bottom:1px solid #eef0f6; }
.co-inputbar .lbl { font-size:12px; font-weight:700; color:#1B84FF; margin-bottom:6px; } .co-inputbar .opt { color:#8a94a6; font-weight:400; }
.co-inputbar .row { display:flex; align-items:center; gap:8px; }
.co-docs { display:flex; align-items:center; gap:8px; padding:10px 14px; border-bottom:1px solid #eef0f6; overflow-x:auto; }
.cdd-zoom {
  margin-left: auto;
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: 2px;
  background: #f2f4f6;
  border-radius: 10px;
  padding: 2px;
}
.cdd-zoom-btn, .cdd-zoom-pct {
  border: none;
  background: transparent;
  cursor: pointer;
  font-weight: 800;
  color: #4b5563;
  border-radius: 8px;
  line-height: 1;
}
.cdd-zoom-btn {
  min-width: 36px;
  height: 32px;
  font-size: 15px;
}
.cdd-zoom-btn:hover:not(:disabled) { background: #fff; color: #191f28; }
.cdd-zoom-btn:disabled { opacity: 0.35; cursor: default; }
.cdd-zoom-pct {
  min-width: 52px;
  height: 32px;
  font-size: 12px;
  color: #3182f6;
}
.cdd-zoom-pct:hover { background: #fff; }
.paper-zoom { max-width: 820px; margin: 0 auto; }
.paper-zoom .paper { max-width: none; margin: 0; }
.doc-tab { display:flex; align-items:center; gap:6px; border:1px solid #e2e6ef; border-radius:8px; padding:5px 9px; cursor:pointer; white-space:nowrap; }
.doc-tab.active { border-color:#1B84FF; background:#eaf3ff; }
.dt-type { font-size:10px; font-weight:700; color:#1B84FF; background:#eaf3ff; border-radius:5px; padding:1px 5px; }
.dt-name { font-size:12px; font-weight:600; max-width:160px; overflow:hidden; text-overflow:ellipsis; cursor:text; }
.dt-rename { font-size:12px; font-weight:600; border:1px solid #1B84FF; border-radius:5px; padding:1px 5px; width:150px; outline:none; }
.dt-prog { font-size:11px; color:#8a94a6; } .dt-del { color:#b3bccd; } .dt-edit { color:#b3bccd; } .dt-edit:hover { color:#1B84FF; }
.co-doc-body { padding:20px; background:#eceff5; }
.co-empty { text-align:center; color:#8a94a6; padding:60px 20px; display:flex; flex-direction:column; align-items:center; gap:10px; }
/* 빈 상태 / 위저드 — 3개 유형 카드 (첫 진입 발견성) */
.cc-studio-empty { padding:56px 20px; text-align:center; display:flex; flex-direction:column; align-items:center; gap:8px; }
.cc-studio-empty .ccs-title { font-size:16px; font-weight:800; color:#191f28; margin-top:4px; }
.cc-studio-empty .ccs-sub { font-size:13px; color:#8b95a1; }
.co-choose { padding:48px 20px; text-align:center; }
.co-choose .cc-title { font-size:16px; font-weight:700; color:#1f2937; }
.co-choose .cc-sub { font-size:13px; color:#8a94a6; margin-top:4px; margin-bottom:22px; }
.cc-cards { display:flex; gap:16px; justify-content:center; flex-wrap:wrap; }
.cc-card { width:190px; padding:22px 16px; background:#fff; border:1.5px solid #e2e6ef; border-radius:12px; cursor:pointer; display:flex; flex-direction:column; align-items:center; gap:8px; transition:all .15s; }
.cc-card:hover { border-color:#1B84FF; box-shadow:0 6px 18px rgba(27,132,255,.15); transform:translateY(-2px); }
.cc-card-label { font-size:14px; font-weight:700; color:#10305c; }
.cc-card-desc { font-size:12px; color:#8a94a6; line-height:1.35; }

/* ── 초기 셋업 위저드 ── */
.cdd-wizard { padding:28px 20px 40px; }
.wz-steps { display:flex; align-items:center; justify-content:center; gap:10px; max-width:560px; margin:0 auto 28px; }
.wz-step { display:flex; align-items:center; gap:7px; font-size:13px; font-weight:600; color:#9aa3b2; white-space:nowrap; }
.wz-step .wz-num { width:22px; height:22px; border-radius:50%; background:#e2e6ef; color:#8a94a6; display:inline-flex; align-items:center; justify-content:center; font-size:12px; }
.wz-step.active { color:#1B84FF; } .wz-step.active .wz-num { background:#1B84FF; color:#fff; }
.wz-step.done { color:#22a06b; } .wz-step.done .wz-num { background:#22a06b; color:#fff; }
.wz-bar { flex:1; height:2px; background:#e2e6ef; max-width:60px; } .wz-bar.done { background:#22a06b; }
.wz-card { max-width:460px; margin:0 auto; background:#fff; border:1px solid #eef0f6; border-radius:14px; padding:28px 26px; box-shadow:0 4px 20px rgba(20,30,60,.06); text-align:center; }
.wz-card.wz-card-wide { max-width:660px; }
.wz-h { font-size:17px; font-weight:700; color:#1f2937; }
.wz-sub { font-size:13px; color:#8a94a6; margin-top:6px; margin-bottom:20px; }
.wz-input { text-align:left; }
.wz-entity { display:flex; align-items:center; gap:10px; background:#f0f5ff; border-radius:10px; padding:12px 14px; font-size:14px; font-weight:600; color:#10305c; }
.wz-selectbtn { width:100%; height:48px; }
.wz-actions { display:flex; align-items:center; gap:8px; margin-top:22px; }
.co-doctitle { max-width:820px; margin:0 auto 12px; display:flex; align-items:center; gap:10px; }
.co-doctitle .dt-label { font-size:12px; font-weight:700; color:#5b6472; white-space:nowrap; }
.co-doctitle .dt-input { background:#fff; border-radius:8px; }
.paper { background:#fff; max-width:820px; margin:0 auto; padding:34px 38px; box-shadow:0 4px 24px rgba(20,30,60,.12); font-family:Arial,'Malgun Gothic',sans-serif; color:#111; font-size:12px; line-height:1.4; }
.co-footer { display:flex; justify-content:space-between; align-items:center; padding:12px 16px; border-top:1px solid #eef0f6; }
.co-foot-right { display:flex; align-items:center; gap:12px; }
.co-progress { font-size:12px; color:#5b6472; max-width:420px; }
.co-progress.err { color:#d64545; font-weight:600; }
</style>

<!-- 문서 표 공용 스타일 (non-scoped) -->
<style>
.cdd-title { text-align:center; font-weight:700; font-size:15px; padding:8px; border:1px solid #000; }
.cdd-tbl { width:100%; border-collapse:collapse; margin-top:-1px; }
.cdd-tbl td, .cdd-tbl th { border:1px solid #000; padding:5px 8px; vertical-align:top; text-align:left; font-weight:400; }
.cdd-sec { font-weight:700; background:#f2f2f2; }
.cdd-lcell { width:42%; }
.cdd-cn { color:#333; }
.cdd-intro { font-size:11px; line-height:1.45; }
.cdd-cbx { display:inline-flex; align-items:center; margin-right:14px; white-space:nowrap; cursor:pointer; }
.cdd-cbx .box { display:inline-flex; margin-right:4px; vertical-align:-3px; }
.cdd-decl { margin-top:14px; font-size:11.5px; } .cdd-decl .sig-line { margin:16px 0 2px; }
.cdd-date-row { margin-top:8px; font-weight:600; }
</style>
